'use client';

import Image from 'next/image';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import { useState } from 'react';

interface TeamMember {
  id: string;
  name: string;
  title: string;
  image: string;
  alt: string;
  bio: string;
  email?: string;
  imageBg?: string;
}

const teamMembers: TeamMember[] = [
  {
    id: 'mirco-lehr',
    name: 'Mirco Lehr',
    title: 'Founder',
    image: '/assets/images/mirco-lehr-rechtsanwalt_large-1775817653726.jpg',
    alt: 'Mirco Lehr – Founder of Cyfire International, Rechtsanwalt und Gründer der cyfire Rechtsanwaltsgesellschaft mbH, spezialisiert auf Urheberrecht, Markenrecht und internationales Wirtschaftsrecht',
    bio: 'I am an attorney, entrepreneur, and founder of cyfire Rechtsanwaltsgesellschaft mbH. I specialize in copyright law, trademark law, international business law, and the enforcement of claims on the internet. For many years, I have represented photographers, companies, and international clients in cross-border legal disputes, particularly in matters involving online platforms, domain name disputes, and international enforcement. In addition to my work as an attorney, I am the founder of Advocall, a legal-tech platform that makes access to legal advice easier for both businesses and private individuals. Furthermore, I initiated Cyfire International, an international network of attorneys in various countries with a focus on corporate law, tax law, compliance, intellectual property, and cross-border disputes. The goal of Cyfire International is to provide clients worldwide with coordinated legal support and to handle international matters efficiently and in a practical manner.',
    email: 'info@cyfire-international.com',
  },
  {
    id: 'annika-kreusch',
    name: 'Annika Kreusch',
    title: 'Head of Business Development & Intellectual Property',
    image: '/assets/images/Photo_AK_2-1776155069196.jpg',
    alt: 'Annika Kreusch – Head of Business Development & Intellectual Property at Cyfire International, passionate creator and connector with expertise in music industry, legal tech and digital business',
    bio: 'Passionate creator, diagonal thinker and connector of dots, with a strong focus on business development, operational efficiency and building practical solutions. For the past fifteen years, I have worked at the intersection of the music industry, legal tech and digital business, with a particular focus on intellectual property, sales and international business development. Throughout my career, I have founded and developed several companies and projects, always driven by the goal of turning complex ideas into scalable, successful ventures. I enjoy bringing together the right people, strategies and structures to create long-term value and sustainable growth. Today, I serve as Head of Business Development & Intellectual Property at Cyfire International, Director of AK Copyright, and Head of Strategic Partnerships at Advocall. In these roles, I focus on building international partnerships, creating new business models and developing innovative approaches in the fields of intellectual property, company formation and international expansion.',
    email: 'annika.kreusch@cyfire.net',
  },
  {
    id: 'emre-kuecuek',
    name: 'Emre Kücük',
    title: 'Head of Legal Partner',
    image: '/assets/images/Emre-1775806173152.png',
    alt: 'Emre Kücük – Head of Legal Partner at Cyfire International, entrepreneur and networker with over 15 years of experience building partnerships across Germany and Europe',
    bio: 'I am an entrepreneur, a networker – and yes, a dealmaker. For more than fifteen years, I have been building companies, creating partnerships and bringing the right people together. Along the way, I have learned one thing above all: trust is the foundation of every good deal. At Cyfire International, I am responsible for building and maintaining our partner network. My goal is to create long-term partnerships from which all sides truly benefit – not only on paper, but in practice. What drives me is the combination of strategic thinking and an understanding of people. I think in solutions, act directly and bring with me a strong network that I actively use every day. Germany, Europe – I know the markets, and I know the people who move within them.',
    email: 'ek@cyfire.net',
  },
  {
    id: 'oemer-kaya',
    name: 'Ömer Kaya',
    title: 'International Business Advisor',
    image: '/assets/images/D3FDE314-FDFE-46AA-B720-60B1C593CFE1_6-1776073198710.jpg',
    alt: 'Ömer Kaya – International Business Advisor at Cyfire International, entrepreneur and strategy advisor with more than 20 years of experience in international business and financial services',
    bio: 'I am an entrepreneur and strategy advisor with more than 20 years of experience in the international business environment. My professional background is shaped by decades of experience in the financial services industry and international business. My focus lies in the development and implementation of tax-efficient and structurally optimized corporate architectures between Germany and the Gulf region. I work at the intersection of strategy, structure, and operational execution. My expertise has enabled me to build a global network of specialists, executives, and thought leaders. Alongside my entrepreneurial activities, I am the author of several books on personal development and self-leadership, in which I draw on my own experiences from economic and personal crises and translate them into practical strategies. In addition, I am active as a producer in the field of digital media, where I develop formats that connect economic, social, and personal topics. Currently, I contribute my legal and business expertise as a business advisor at Cyfire International. In this role, I support companies with complex strategic, structural, and international matters and am valued for my broad expertise and strategic perspective on challenging situations.',
    email: 'oemer.kaya@kanzlei-lehr.com',
  },
  {
    id: 'dirk-semper',
    name: 'Dirk Semper',
    title: 'Case Manager for Labor Law and International Business Setup',
    image: '/assets/images/Fotoprivat-1776078350975.jpg',
    alt: 'Dirk Semper – Case Manager for Labor Law and International Business Setup at Cyfire International, experienced expert in labor law, shift planning, personnel scheduling, and international company formation in Southeast Asia',
    bio: 'I am Dirk Semper, an experienced expert in labor law, specializing in shift planning, personnel scheduling, and change management. Since 2024, I have been working as a labor law clerk at the law firm Lime-Law and support Cyfire International on a freelance basis in setting up international companies, particularly in Southeast Asia. With long-standing expertise from the telecommunications industry (including at Telefonica Germany) and an international focus (currently based in Thailand), I advise on cross-border structures, contract drafting, and compliance issues. My passions include cryptocurrencies, technology, and efficient process optimization.',
    email: 'dirk.semper@lime-law.de',
    imageBg: '#2a2a2a',
  },
  {
    id: 'kristina-metuiwe',
    name: 'Kristina Metuiwe',
    title: 'International Business Consultant - Dubai Office',
    image: '/assets/images/Bild_Website-1776154375763.png',
    alt: 'Kristina Metuiwe – International Business Consultant at Cyfire International Dubai Office, founder of KMW Consulting LLC FZ, specializing in international law firm partnerships, company formation in Dubai, and real estate investments',
    bio: 'I am the founder of KMW Consulting LLC FZ, based in Dubai, supporting clients in international projects, business structuring, and investments. In cooperation with the German law firm Cyfire International, my work focuses on three core areas:\n\n• International Law Firm Partnerships: Supporting Cyfire International in building international partnerships by identifying suitable law firms worldwide. I structure the initial contact and support the development of long-term cooperation.\n\n• Company Formation & Relocation to Dubai: I assist entrepreneurs and private clients throughout the entire setup process—from selecting the right structure to company formation, visas, and ongoing support.\n\n• Real Estate Investments & Networking: I coordinate access to real estate investment opportunities in Dubai and connect international investors with developers, brokers, and relevant market participants.\n\nWith a strong international network and multilingual advisory services (German, English, Russian), I ensure the efficient execution of complex cross-border projects.',
    email: 'km@cyfire.net',
  },
  {
    id: 'philipp-steiner',
    name: 'Philipp Steiner',
    title: 'Tax, Accounting and Business Advisor',
    image: '/assets/images/Sophisticated_portrait_of_a_man-1776182296728.png',
    alt: 'Philipp Steiner – Tax, Accounting and Business Advisor at Cyfire International, Swiss Certified Fiduciary with over 15 years of experience in accounting, tax, and ERP-based process automation',
    bio: 'Experienced Swiss finance professional with over 15 years of diverse experience in accounting, tax, financial operations, and ERP-based process automation. Holder of the Swiss Federal Diploma as Certified Fiduciary (Treuhänder mit eidg. Fachausweis). Proven ability to lead multi-jurisdictional accounting processes, standardize financial workflows, and implement ERP systems, with recent hands-on experience in remote financial roles across Switzerland and Germany. Bilingual in German and English, comfortable navigating matrix organizations, and passionate about driving operational excellence.',
    email: 'philipp.steiner@lehr.law',
  },
];

const COLLAPSED_LINES = 5;

function TeamMemberCard({ member, index }: { member: TeamMember; index: number }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <article
      key={member.id}
      className="group bg-card border border-border rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 animate-fade-in flex flex-col"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="relative w-full aspect-[4/5] overflow-hidden bg-muted" style={member.imageBg ? { backgroundColor: member.imageBg } : {}}>
        <Image
          src={member.image}
          alt={member.alt}
          fill
          className="object-cover object-top group-hover:scale-105 transition-transform duration-500"
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
        />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-card/80 to-transparent" />
      </div>

      <div className="p-6 md:p-8 flex flex-col flex-1">
        <div className="mb-5">
          <h2 className="text-2xl font-serif font-semibold text-foreground mb-1">
            {member.name}
          </h2>
          <span className="inline-block px-3 py-1 bg-secondary/10 text-secondary border border-secondary/30 rounded-full text-xs font-semibold uppercase tracking-wider">
            {member.title}
          </span>
        </div>

        <div className="flex-1 mb-4">
          <div
            className={`overflow-hidden transition-all duration-300 ${
              expanded ? '' : `line-clamp-${COLLAPSED_LINES}`
            }`}
            style={!expanded ? { display: '-webkit-box', WebkitLineClamp: COLLAPSED_LINES, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}}
          >
            <div className="text-sm text-muted-foreground leading-relaxed space-y-2">
              {member.bio.split('\n\n').map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold border border-primary/20 bg-primary/5 text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-200 shadow-sm hover:shadow-md"
          >
            {expanded ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Less
              </>
            ) : (
              <>
                Read more
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </>
            )}
          </button>
        </div>

        {member.email && (
          <a
            href={`mailto:${member.email}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-secondary transition-colors group/link mt-auto"
          >
            <Icon name="EnvelopeIcon" size={16} className="text-secondary" />
            <span className="group-hover/link:underline underline-offset-2">{member.email}</span>
          </a>
        )}
      </div>
    </article>
  );
}

export default function TeamPageClient() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="relative pt-32 pb-16 px-6 md:px-12 max-w-8xl mx-auto">

          <div className="relative z-10 text-center max-w-3xl mx-auto animate-fade-in">
            <span className="inline-block px-4 py-2 border border-primary/30 rounded-full text-xs uppercase tracking-widest text-primary mb-6">
              Our People
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-light leading-[0.95] tracking-tight text-foreground mb-6">
              The Cyfire{' '}
              <span className="italic text-primary">International</span>
              <br />
              Team
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto border-l-2 border-secondary pl-6 text-left">
              We bring together entrepreneurial thinking, strategic expertise, and a shared belief that trust is the foundation of every strong partnership.
            </p>
          </div>
        </section>

        <section className="pb-24 px-6 md:px-12 max-w-8xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 items-stretch">
            {teamMembers.map((member, index) => (
              <TeamMemberCard key={member.id} member={member} index={index} />
            ))}
          </div>
        </section>

        <section className="py-20 px-6 md:px-12 bg-secondary/10 border-t border-secondary/20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-light mb-4 text-foreground">
              Work With Our Team
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
              Ready to build a partnership that truly benefits all sides? Get in touch with us today.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 bg-secondary text-secondary-foreground px-8 py-3 rounded-full font-semibold hover:bg-secondary/90 transition-all shadow-lg hover:shadow-xl hover:scale-105"
            >
              Schedule a Consultation
              <Icon name="ArrowRightIcon" size={16} />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
